import React, { useEffect, useState } from "react";
import { ActivityCard, MixedText, PlayButton, ProgressPill } from "../shared";

/**
 * SuffixBuilder — "The ة Comes Untied" possessive-ending builder.
 *
 * One build at a time: an English target ("her coffee") and the bare base
 * word are shown, and the learner taps the owner ending that produces it.
 * The right ending snaps the built word together — suffix highlighted, the
 * ة→ت morph called out in a note — and plays it. Completes when every word
 * has been built.
 */
export default function SuffixBuilder({ data, onComplete, audio }) {
  const [idx, setIdx] = useState(0);
  const [solved, setSolved] = useState(false);
  const [wrongId, setWrongId] = useState(null);
  const [done, setDone] = useState(false);

  const builds = data.builds;
  const build = builds[idx];
  const isLast = idx >= builds.length - 1;

  useEffect(() => {
    if (done) onComplete();
  }, [done, onComplete]);

  const handlePick = (suffix) => {
    if (solved) return;
    if (suffix.id === build.suffix_id) {
      setSolved(true);
      setWrongId(null);
      audio.play(build.full_ar);
    } else {
      setWrongId(suffix.id);
      setTimeout(() => setWrongId(null), 600);
    }
  };

  const handleNext = () => {
    if (isLast) {
      setDone(true);
    } else {
      setIdx(idx + 1);
      setSolved(false);
    }
  };

  const suffixBare = build.suffix_id
    ? (data.suffixes.find((s) => s.id === build.suffix_id)?.suffix || "").replace(/^ـ/, "")
    : "";
  const stem = build.full_ar.slice(0, build.full_ar.length - suffixBare.length);

  return (
    <ActivityCard>
      <div className="flex items-center justify-end mb-4">
        <ProgressPill>{Math.min(idx + (solved ? 1 : 0), builds.length)}/{builds.length} words built</ProgressPill>
      </div>

      <div className="text-center">
        <p className="text-sm m-0" style={{ color: "var(--parchment-dim)" }}>Build:</p>
        <p className="text-xl mt-1 mb-3 font-semibold" style={{ color: "var(--parchment)" }}>
          {build.prompt_en}
        </p>

        {solved ? (
          <>
            <p dir="rtl" lang="ar" className="font-arabic text-6xl leading-[1.6] m-0" style={{ color: "var(--parchment)" }}>
              {stem}
              <span style={{ color: "var(--tag-violet)" }}>{suffixBare}</span>
            </p>
            <p className="text-base mt-2 mb-0 inline-flex items-center gap-3" style={{ color: "var(--parchment)" }}>
              {build.prompt_en}
              {audio.audioAllowed && <PlayButton text={build.full_ar} playingText={audio.playingText} onPlay={audio.play} />}
            </p>
            {build.note && (
              <p className="text-sm mt-2 mb-0 ml-3 px-4 py-2 rounded-lg inline-block" style={{ background: "var(--gold-bg)", color: "var(--parchment)" }}>
                <MixedText text={build.note} />
              </p>
            )}
          </>
        ) : (
          <p dir="rtl" lang="ar" className="font-arabic text-6xl leading-[1.6] m-0" style={{ color: "var(--parchment)" }}>
            {build.base_ar}
            <span className="animate-pulse" style={{ color: "var(--gold-dim)" }}> + ؟</span>
          </p>
        )}
        {!solved && (
          <p className="text-xs mt-1 mb-0" style={{ color: "var(--parchment-dim)" }}>
            {build.base_ar} — {build.base_en}. Tap the ending that makes it "{build.prompt_en}".
          </p>
        )}
      </div>

      <div dir="rtl" className="flex flex-wrap justify-center gap-2 mt-5">
        {data.suffixes.map((suffix) => (
          <button
            key={suffix.id}
            type="button"
            onClick={() => handlePick(suffix)}
            disabled={solved}
            className="rounded-xl px-3.5 py-2 text-center transition-all hover:brightness-110 disabled:cursor-default"
            style={{
              background: "var(--ink)",
              border:
                wrongId === suffix.id
                  ? "1px solid #dc5a5a"
                  : solved && suffix.id === build.suffix_id
                  ? "1px solid var(--tag-violet)"
                  : "1px solid var(--panel-line)",
              opacity: solved && suffix.id !== build.suffix_id ? 0.45 : 1,
            }}
          >
            <span lang="ar" className="font-arabic text-2xl leading-[1.6] block" style={{ color: "var(--parchment)" }}>
              {suffix.suffix}
            </span>
            <span className="text-[11px] block" style={{ color: "var(--parchment-dim)" }}>{suffix.en}</span>
          </button>
        ))}
      </div>

      {solved && !done && (
        <div className="flex justify-center mt-5">
          <button
            type="button"
            onClick={handleNext}
            className="inline-flex items-center justify-center px-5 py-2.5 min-h-[44px] text-sm font-semibold rounded-lg transition-[filter] hover:brightness-105"
            style={{ background: "var(--gold)", color: "var(--ink)" }}
          >
            {isLast ? "Finish" : "Next word"}
          </button>
        </div>
      )}

      {done && (
        <div
          className="flex items-start gap-2.5 mt-4 px-4 py-3 rounded-xl"
          style={{ background: "var(--violet-bg)", border: "1px solid var(--panel-line)" }}
        >
          <span className="text-lg leading-none" aria-hidden="true">🪢</span>
          <p className="text-sm leading-relaxed m-0" style={{ color: "var(--parchment)" }}>
            <MixedText text={data.takeaway} />
          </p>
        </div>
      )}
    </ActivityCard>
  );
}
